/**
 * Markdown Serializer for TipTap Prompt Editor
 *
 * Converts TipTap JSON content to Markdown format with metadata preservation.
 * Used for exporting prompts to a human-readable/editable format that can be
 * round-tripped back to TipTap JSON.
 *
 * Key features:
 * - Preserves section metadata (id, deployment settings, readonly, etc.)
 * - Exports all tabs with deployment type markers
 * - Converts mentions to their appropriate syntax
 * - Handles nested lists and all block types
 */

import {
  PROMPT_MARK_NAMES as MARK_NAMES,
  PROMPT_NODE_NAMES as NODE_NAMES
} from "./agent-prompt.constants";
import {
  type SectionAttrs,
  type TabPanelAttrs,
  type TipTapJSONContent
} from "./agent-prompt.types";
import { isEmptyParagraph } from "./agent-prompt-conversion.utils";
import {
  buildSectionDirective,
  buildTabDirective,
  createOrderedListCounters
} from "./agent-prompt-markdown.constants";

// ============================================================================
// Types
// ============================================================================

// Mark type derived from TipTapJSONContent to avoid duplication
type Mark = NonNullable<TipTapJSONContent["marks"]>[number];

// Use Partial<> since attrs from JSON may have missing fields
type SerializerSectionAttrs = Partial<SectionAttrs>;
type SerializerTabPanelAttrs = Partial<TabPanelAttrs>;

// ============================================================================
// Constants
// ============================================================================

/**
 * Ordered list counters using "." delimiter (e.g., "1.", "a.", "i.")
 * Shared logic via createOrderedListCounters from @nexus/types
 */
const ORDERED_LIST_COUNTERS = createOrderedListCounters(".");

// ============================================================================
// Shared Utilities (exported for markdown-manager.ts & frontend extensions)
// ============================================================================

/**
 * Matches an http(s) URL. URLs are autolinked by the markdown parser (GFM
 * autolink), and the parser does NOT unescape backslash sequences inside an
 * autolink — so a URL must be emitted verbatim, never markdown-escaped. Stops at
 * whitespace, `<` (angle-bracket autolink delimiter), and backtick (invalid in a
 * URL and a markdown special).
 */
const URL_PATTERN = /\bhttps?:\/\/[^\s<`]+/gi;

/**
 * Every span that must reach the markdown VERBATIM rather than markdown-escaped:
 * a URL, or a `{{variable}}` mention written as prose.
 *
 * The two are here for one reason. Both are re-read by a tokenizer that does NOT
 * unescape — GFM autolink for the URL, `VARIABLE_MENTION_PATTERN` for the
 * mention — so a backslash the serializer adds inside one is not undone on the
 * way back in. It becomes part of the thing: `{{document_title}}` typed into the
 * editor is stored as `{{document\_title}}` and parses back as a mention whose
 * NAME carries a backslash, matching no registered variable (NEX-4374). The URL
 * half of this is NEX-2288 and the mechanism is identical.
 *
 * A mention typed as prose is the only path that reaches this. One picked from
 * the mention menu is a node, and `renderVariableMention` emits it directly
 * without ever passing through here — which is exactly why pasting round-tripped
 * cleanly while typing did not.
 *
 * `[^}\n]+` deliberately refuses a newline where the parser's own pattern allows
 * one: over-exempting would hand a stray `{{` in prose a licence to swallow the
 * paragraph after it, and an unclosed brace is prose that should still be
 * escaped.
 */
const VERBATIM_SPAN_PATTERN = /\bhttps?:\/\/[^\s<`]+|\{\{[^}\n]+\}\}/gi;

/**
 * Matches a backslash that escapes a markdown-special character. Used to strip
 * accumulated escapes from inside a URL (see escapeMarkdownText / NEX-2288).
 */
const MARKDOWN_ESCAPE_SEQUENCE = /\\([\\*_~`[\]])/g;

/** Applies the raw 7-char markdown escape (\ * _ ~ ` [ ]) to a prose segment. */
function escapeMarkdownSpecials(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/\*/g, "\\*")
    .replace(/_/g, "\\_")
    .replace(/~/g, "\\~")
    .replace(/`/g, "\\`")
    .replace(/\[/g, "\\[")
    .replace(/\]/g, "\\]");
}

/**
 * Removes markdown backslash-escapes, to a fixed point.
 *
 * Used for text that can never legitimately carry a markdown escape, so any escape
 * present is accumulated corruption rather than intent:
 *  - inside a URL, where the parser autolinks and never unescapes (NEX-2288);
 *  - inside raw markdown source or a section title, when repairing prompts corrupted
 *    before the round-trip was made idempotent (NEX-2564).
 *
 * Runs to a fixed point because the corruption nests: `\\\_` → `\_` → `_`.
 *
 * Exported for the one-off repair of already-corrupted prompts. It is NOT safe on
 * prose: a text node may legitimately hold a backslash the author typed, and this
 * would eat it. Only call it where an escape cannot be authored.
 */
export function stripMarkdownEscapes(text: string): string {
  let result = text;
  let prev: string;
  do {
    prev = result;
    result = result.replace(MARKDOWN_ESCAPE_SEQUENCE, "$1");
  } while (result !== prev);
  return result;
}

/**
 * Escapes special markdown characters in text.
 * 7-char escape: \ * _ ~ ` [ ]
 *
 * URLs and `{{variable}}` mentions are exempt, for one reason: the parser reads
 * both back with a tokenizer that does NOT unescape, so a backslash added inside
 * one survives into the value rather than being undone. A URL's underscores both
 * break the link and accumulate on every save round-trip (fr_BE → fr\_BE →
 * fr\\\_BE …, NEX-2288); a typed `{{document_title}}` is stored as
 * `{{document\_title}}` and parses back as a mention whose name carries the
 * backslash, so it matches no registered variable (NEX-4374).
 *
 * Both are passed through verbatim, with any previously-accumulated escapes
 * stripped — which heals text corrupted before these fixes. Only the surrounding
 * prose is escaped. See {@link VERBATIM_SPAN_PATTERN}.
 *
 * Shared between markdown-serializer (backend) and MarkdownManager (frontend)
 * to guarantee identical escaping in both serialization pipelines.
 */
export function escapeMarkdownText(text: string): string {
  return mapAroundPattern(
    text,
    VERBATIM_SPAN_PATTERN,
    escapeMarkdownSpecials,
    stripMarkdownEscapes
  );
}

/**
 * Strips accumulated markdown escapes from the URLs in `text`, leaving prose untouched.
 *
 * The serializer already heals URLs on the way out (see {@link escapeMarkdownText}), so
 * emitted markdown looks clean — but the STORED text node stays corrupt, and the LLM
 * prompt is built from `promptJson` by `convertPromptToAIText`, which reads text nodes
 * verbatim and never goes through the serializer. A URL corrupted before NEX-2288 is
 * therefore still being handed to the model with its backslashes in.
 *
 * Exported for the one-off repair of already-corrupted prompts. Safe on prose because
 * only URL substrings are touched, and a valid http(s) URL never needs a markdown
 * escape — which is the same reasoning that scopes the serializer's healing to URLs.
 */
export function stripMarkdownEscapesInUrls(text: string): string {
  return mapAroundPattern(text, URL_PATTERN, (prose) => prose, stripMarkdownEscapes);
}

/**
 * Splits `text` on `pattern`, mapping the matched spans and the prose between
 * them with different functions.
 *
 * `matchAll` requires a global pattern and iterates over its own internal clone,
 * so a module-level regex passed here keeps no `lastIndex` between calls.
 */
function mapAroundPattern(
  text: string,
  pattern: RegExp,
  onProse: (segment: string) => string,
  onSpan: (span: string) => string
): string {
  let result = "";
  let lastIndex = 0;

  for (const match of text.matchAll(pattern)) {
    const start = match.index ?? 0;
    result += onProse(text.slice(lastIndex, start));
    result += onSpan(match[0]);
    lastIndex = start + match[0].length;
  }
  result += onProse(text.slice(lastIndex));

  return result;
}

/**
 * Strips invalid C0 control characters (U+0000–U+001F) from serialized text,
 * preserving tab (U+0009) and newline (U+000A) which markdown uses for structure.
 *
 * TipTap text nodes can carry raw control characters from pasted content, and the
 * serializer copies node text verbatim. Such characters are non-printable garbage
 * that have no markdown meaning, and they break strict RFC 8259 consumers (e.g. jq)
 * the moment any serializer in the chain emits them unescaped — see NEX-2289, where
 * `GET /agents/:id` returned a `.prompt` string that jq refused to parse. Removing
 * them at the single serialization chokepoint keeps every prompt-emitting endpoint
 * (agent, agent version, cue editor, prompt assistant) clean and parseable.
 */
export function stripInvalidControlChars(text: string): string {
  // eslint-disable-next-line no-control-regex -- intentionally targeting C0 controls
  return text.replace(/[\u0000-\u0008\u000B-\u001F]/g, "");
}

// ============================================================================
// Mark Application
// ============================================================================

/**
 * Delimiters for each formatting mark, and the order they nest in — outermost first.
 *
 * A fixed order is what makes the output canonical: bold+italic always serializes as
 * `**_x_**`, never `_**x**_`, so re-parsing yields the same marks and the next save
 * emits the same bytes. RAW carries no delimiter (see {@link PROMPT_MARK_NAMES}).
 */
const MARK_DELIMITERS: Record<string, string> = {
  [MARK_NAMES.BOLD]: "**",
  [MARK_NAMES.ITALIC]: "_",
  [MARK_NAMES.STRIKE]: "~~",
  [MARK_NAMES.CODE]: "`"
};

const MARK_NESTING_ORDER: readonly string[] = [
  MARK_NAMES.BOLD,
  MARK_NAMES.ITALIC,
  MARK_NAMES.STRIKE,
  MARK_NAMES.CODE
];

/** Formatting marks on a node, canonically ordered outermost-first. Unknown marks are dropped. */
function orderedMarkNames(marks: Mark[] | undefined): string[] {
  if (!marks || marks.length === 0) return [];
  const present = new Set(marks.map((m) => m.type));
  return MARK_NESTING_ORDER.filter((name) => present.has(name));
}

function hasMark(node: TipTapJSONContent, markName: string): boolean {
  return node.marks?.some((m) => m.type === markName) ?? false;
}

/**
 * Wraps text in markdown mark syntax (bold, italic, strike, code).
 *
 * Prefer {@link serializeInlineContent}, which applies marks across a *run* of nodes.
 * This helper wraps a single string and cannot see its neighbours, so using it per
 * node re-opens and re-closes a mark that spans several nodes — `**a `b` c**` parses
 * into three bold text nodes and comes back as `**a ****`b`**** c**`, whose `****`
 * runs then double on every save (NEX-2564).
 *
 * Retained for callers that genuinely hold one isolated string.
 *
 * @param text - The text content to wrap
 * @param marks - Array of marks to apply
 * @returns Text with markdown formatting applied
 */
export function applyMarksToText(text: string, marks: Mark[] | undefined): string {
  if (!marks || marks.length === 0 || !text) {
    return text;
  }

  let result = text;

  // Innermost first, so the canonical nesting order ends up outermost-first.
  for (const name of [...orderedMarkNames(marks)].reverse()) {
    const delimiter = MARK_DELIMITERS[name];
    if (delimiter) result = `${delimiter}${result}${delimiter}`;
  }

  return result;
}

// ============================================================================
// Raw Text Serialization (for code content)
// ============================================================================

/**
 * Serializes inline content as raw text without markdown escaping.
 * Used for code blocks and inline code where content is literal.
 *
 * Shared between markdown-serializer (backend) and MarkdownManager (frontend)
 * to guarantee identical raw-text handling in both serialization pipelines.
 *
 * @param content - Array of inline content nodes
 * @returns Raw text string (no escaping)
 */
export function serializeRawText(content: TipTapJSONContent[] | undefined): string {
  if (!content) return "";
  return content
    .map((node) => {
      if (node.type === NODE_NAMES.TEXT) return node.text || "";
      if (node.type === NODE_NAMES.HARD_BREAK) return "\n";
      return "";
    })
    .join("");
}

// ============================================================================
// Inline Content Serialization
// ============================================================================

/** A leaf of inline content: its final text, and the formatting marks that wrap it. */
interface InlineSegment {
  text: string;
  markNames: string[];
  /** An inline mention: atomic, and carries no marks of its own. */
  isMention?: boolean;
}

const INLINE_MENTION_TYPES: ReadonlySet<string> = new Set([
  NODE_NAMES.VARIABLE_MENTION,
  NODE_NAMES.SKILL_MENTION,
  NODE_NAMES.PLACEHOLDER_MENTION
]);

/** Marks a mention may sit inside. Code is excluded: a code span would swallow the mention as literal text. */
const MENTION_BRIDGEABLE_MARKS: ReadonlySet<string> = new Set([
  MARK_NAMES.BOLD,
  MARK_NAMES.ITALIC,
  MARK_NAMES.STRIKE
]);

/**
 * Renders one inline node to its text, ignoring marks — marks are applied across runs
 * by {@link serializeInlineContent}, never per node.
 *
 * Returns `null` for a node this function does not handle, so callers can fall through
 * to a custom renderer.
 */
function renderInlineNodeText(node: TipTapJSONContent): string | null {
  switch (node.type) {
    case NODE_NAMES.TEXT: {
      const text = node.text || "";

      // RAW text is markdown source the parser had no node for (a GFM table, an
      // authored <context> block). It is emitted byte-for-byte: escaping it would
      // re-escape escapes it already contains, and that is the NEX-2564 cascade.
      if (hasMark(node, MARK_NAMES.RAW)) return text;

      // Don't escape text inside inline code — markdown treats backtick content as literal.
      // Escaping would survive the round-trip and accumulate on each copy/paste cycle.
      if (hasMark(node, MARK_NAMES.CODE)) return text;

      return escapeMarkdownText(text);
    }

    case NODE_NAMES.HARD_BREAK:
      return "\n";

    case NODE_NAMES.VARIABLE_MENTION: {
      // Variable mentions render as {{variableName}}
      // Marks are stripped - mentions are atomic
      const variableName = (node.attrs?.variableName as string) || "";
      if (!variableName) return ""; // Skip empty mentions
      return `{{${variableName}}}`;
    }

    case NODE_NAMES.SKILL_MENTION: {
      // Skill mentions render as @skillName (name is already a valid slug)
      // Marks are stripped - mentions are atomic
      const skillName = (node.attrs?.name as string) || "";
      if (!skillName) return ""; // Skip empty mentions
      return `@${skillName}`;
    }

    case NODE_NAMES.PLACEHOLDER_MENTION: {
      // Placeholder mentions render as [text]
      // Marks are stripped - mentions are atomic
      const text = (node.attrs?.text as string) || "";
      if (!text) return ""; // Skip empty mentions
      return `[${text}]`;
    }

    default:
      return null;
  }
}

/** Flattens inline nodes into segments, resolving custom renderers and recursion. */
function collectInlineSegments(
  content: TipTapJSONContent[],
  renderNode: ((node: TipTapJSONContent) => string | null) | undefined,
  out: InlineSegment[]
): void {
  for (const node of content) {
    if (!node.type) continue;

    const custom = renderNode?.(node);
    if (custom !== null && custom !== undefined) {
      // An empty segment must be dropped, not pushed. A zero-length segment carrying no
      // marks makes the stack below close every open delimiter and immediately reopen
      // it, so bold("a") + custom("") + bold("b") would emit `**a****b**` — the exact
      // `****` run this serializer was rewritten to stop producing (NEX-2564). The text
      // branch below drops empties for the same reason.
      if (custom !== "") {
        out.push({ text: custom, markNames: [], isMention: INLINE_MENTION_TYPES.has(node.type) });
      }
      continue;
    }

    const text = renderInlineNodeText(node);
    if (text !== null) {
      if (text !== "") {
        out.push({
          text,
          markNames: markNamesForSegment(node),
          isMention: INLINE_MENTION_TYPES.has(node.type)
        });
      }
      continue;
    }

    // Unknown node that might still carry content — recurse.
    if (node.content) collectInlineSegments(node.content, renderNode, out);
  }
}

/** Marks that wrap a segment. RAW text is verbatim, so it is never wrapped. */
function markNamesForSegment(node: TipTapJSONContent): string[] {
  if (node.type !== NODE_NAMES.TEXT) return [];
  if (hasMark(node, MARK_NAMES.RAW)) return [];
  return orderedMarkNames(node.marks);
}

/** The longest shared outermost-first prefix of two canonically ordered mark lists. */
function commonMarkPrefix(a: readonly string[], b: readonly string[]): string[] {
  let shared = 0;
  while (shared < a.length && shared < b.length && a[shared] === b[shared]) shared++;
  return a.slice(0, shared);
}

/**
 * Lets a mark run continue through a mention instead of closing around it.
 *
 * Mentions carry no marks, so bold text on both sides of `@skill` used to serialize as
 * `**a (**@skill** b)**`. The re-opening `**` is followed by a space, which CommonMark
 * says cannot open emphasis, so the parser read both `**` pairs as literal asterisks,
 * dropped the bold, and the next save escaped them to `\*\*` (NEX-5989). Emitting
 * `**a (@skill b)**` instead parses back to the same nodes: the parser applies marks to
 * text only and leaves the mention bare.
 */
function bridgeMarksAcrossMentions(segments: InlineSegment[]): InlineSegment[] {
  return segments.map((segment, index) => {
    if (!segment.isMention) return segment;
    const before = segments
      .slice(0, index)
      .reverse()
      .find((s) => !s.isMention);
    const after = segments.slice(index + 1).find((s) => !s.isMention);
    if (!before || !after) return segment;
    const shared = commonMarkPrefix(before.markNames, after.markNames);
    const bridgeable = shared.findIndex((name) => !MENTION_BRIDGEABLE_MARKS.has(name));
    const markNames = bridgeable === -1 ? shared : shared.slice(0, bridgeable);
    return markNames.length > 0 ? { ...segment, markNames } : segment;
  });
}

/**
 * Keeps delimiters off whitespace: a mark that opens or closes at a segment edge takes
 * that edge's whitespace outside it, as `a **b** c` rather than `a** b **c`.
 *
 * CommonMark only opens emphasis on a delimiter not followed by whitespace, and only
 * closes it on one not preceded by whitespace, so `** b` or `b **` is literal text to
 * the parser — the marks are lost and the asterisks escaped on the next save
 * (NEX-5989). Whitespace carrying a mark renders identically without it. Code is
 * left alone: backticks have no flanking rule, and a code span keeps its spaces.
 */
function expelEdgeWhitespace(segments: InlineSegment[]): InlineSegment[] {
  const out: InlineSegment[] = [];
  segments.forEach((segment, index) => {
    if (
      segment.isMention ||
      segment.markNames.length === 0 ||
      segment.markNames.includes(MARK_NAMES.CODE)
    ) {
      out.push(segment);
      return;
    }
    const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(segment.text);
    const [, leading = "", body = "", trailing = ""] = match ?? [];
    const prevMarks = out[out.length - 1]?.markNames ?? [];
    const nextMarks = segments[index + 1]?.markNames ?? [];
    if (body === "") {
      out.push({ text: segment.text, markNames: commonMarkPrefix(prevMarks, nextMarks) });
      return;
    }
    if (leading)
      out.push({ text: leading, markNames: commonMarkPrefix(prevMarks, segment.markNames) });
    out.push({ ...segment, text: body });
    if (trailing)
      out.push({ text: trailing, markNames: commonMarkPrefix(segment.markNames, nextMarks) });
  });
  return out;
}

/**
 * Serializes inline content (text nodes, mentions, hard breaks) to markdown.
 *
 * Marks are applied with a stack across *runs* of adjacent segments rather than per
 * node: a delimiter opens when a mark appears and closes only when it stops applying.
 * `**a `b` c**` parses into three text nodes that all carry bold; wrapping each one
 * separately emits `**a ****`b`**** c**`, and those `****` runs double on every
 * subsequent save (NEX-2564). With a stack the bold opens once and closes once.
 *
 * Shared by the backend serializer and the frontend MarkdownManager so both
 * pipelines emit identical markdown — two copies of this logic is how the escaping
 * bug survived in the frontend after the backend was patched.
 *
 * @param content - Array of inline content nodes
 * @param options.renderNode - Custom renderer consulted before the defaults. Return
 *   `null` to fall through.
 * @returns Serialized markdown string
 */
export function serializeInlineContent(
  content: TipTapJSONContent[] | undefined,
  options?: { renderNode?: (node: TipTapJSONContent) => string | null }
): string {
  if (!content) return "";

  const collected: InlineSegment[] = [];
  collectInlineSegments(content, options?.renderNode, collected);
  const segments = expelEdgeWhitespace(bridgeMarksAcrossMentions(collected));

  let result = "";
  let open: string[] = [];

  for (const segment of segments) {
    const wanted = segment.markNames;

    // How much of the currently-open nesting the next segment keeps.
    let shared = 0;
    while (shared < open.length && shared < wanted.length && open[shared] === wanted[shared]) {
      shared++;
    }

    for (let i = open.length - 1; i >= shared; i--) {
      result += MARK_DELIMITERS[open[i] as string] ?? "";
    }
    for (let i = shared; i < wanted.length; i++) {
      result += MARK_DELIMITERS[wanted[i] as string] ?? "";
    }

    result += segment.text;
    open = wanted;
  }

  for (let i = open.length - 1; i >= 0; i--) {
    result += MARK_DELIMITERS[open[i] as string] ?? "";
  }

  return result;
}

function serializeInline(content: TipTapJSONContent[] | undefined): string {
  return serializeInlineContent(content);
}

// ============================================================================
// List Serialization
// ============================================================================

/**
 * The two things a list item needs serialized that differ between the pipelines: the
 * frontend consults its custom node renderers first, the backend has none.
 */
export interface ListSerializerHooks {
  /** Renders a paragraph's inline content. Defaults to this module's own. */
  renderInline?: (content: TipTapJSONContent[] | undefined) => string;
  /** Renders any other block inside an item. Defaults to this module's own. */
  renderBlock?: (node: TipTapJSONContent) => string | null;
}

function isNestedList(node: TipTapJSONContent): boolean {
  return (
    node.type === NODE_NAMES.BULLET_LIST ||
    node.type === NODE_NAMES.ORDERED_LIST ||
    node.type === NODE_NAMES.TASK_LIST
  );
}

/**
 * Serializes list nodes (bullet, ordered, task) with proper nesting
 *
 * Shared by the backend serializer and the frontend MarkdownManager, for the same
 * reason `serializeInlineContent` is: a second copy of it is how NEX-4602 came to be
 * two bugs — the flattening that ate a production prompt lived in both files, and the
 * comment claiming the frontend "only contributes the custom-renderer lookup" was
 * describing the inline half alone.
 *
 * @param node - The list node to serialize
 * @param indent - Current indentation level (0-based)
 * @param inheritedOrderedType - Inherited type from parent ordered list for nested lists
 * @param hooks - Per-pipeline renderers; see {@link ListSerializerHooks}
 * @returns Serialized markdown string
 */
export function serializeListContent(
  node: TipTapJSONContent,
  indent: number = 0,
  inheritedOrderedType?: string,
  hooks?: ListSerializerHooks
): string {
  const renderInline = hooks?.renderInline ?? serializeInline;
  const renderBlock = hooks?.renderBlock ?? serializeSingleBlock;

  if (!node.content) return "";

  const indentStr = "    ".repeat(indent);
  const listType = node.type;
  // Nested ordered lists inherit parent's type if they don't have their own
  const orderedListType = (node.attrs?.type as string) || inheritedOrderedType || "1";

  return node.content
    .map((item, index) => {
      // Only process list items and task items
      if (item.type !== NODE_NAMES.LIST_ITEM && item.type !== NODE_NAMES.TASK_ITEM) {
        return "";
      }

      // What opens the item, and the column its content therefore starts at. The two
      // come apart on a task item: `- [ ]` opens it, but only `- ` is the list MARKER —
      // the checkbox is the item's first content, so continuation lines align at 2, not
      // at 6. Indenting them to 6 pushes them four columns past the content column,
      // which is an indented code block, and it swallowed the fence that followed.
      let prefix: string;
      let markerWidth: number;
      if (listType === NODE_NAMES.ORDERED_LIST) {
        const defaultCounter = (n: number) => `${n}.`;
        const counterFn = ORDERED_LIST_COUNTERS[orderedListType] ?? defaultCounter;
        // Not `|| 1`: that reads a list the author numbered from 0 as unnumbered, and the
        // parser keeps the number a list starts at. Only the numeric style has a 0 — no
        // letter or numeral comes before a or i.
        const storedStart = node.attrs?.start;
        const lowestStart = orderedListType === "1" ? 0 : 1;
        const start =
          typeof storedStart === "number" && storedStart >= lowestStart ? storedStart : 1;
        prefix = counterFn(start + index);
        markerWidth = prefix.length + 1;
      } else if (listType === NODE_NAMES.TASK_LIST) {
        // GFM needs the bullet: `[x] done` on its own is not a list at all, and the
        // parser read the bare brackets back as a placeholder mention, so one save
        // turned a task list into a paragraph. Idempotent afterwards — which is why
        // the round-trip corpus never caught it.
        const isChecked = item.attrs?.checked === true;
        prefix = isChecked ? "- [x]" : "- [ ]";
        markerWidth = 2;
      } else {
        // Bullet list, and the fallback for an unrecognised list type.
        prefix = "-";
        markerWidth = 2;
      }

      // Handle continuation lines (hardBreak within list items) and the item's own
      // blocks. Every line after the first is indented to the item's content column so
      // it stays part of this item. A blank line is left blank — padding it to the
      // content column writes trailing whitespace into the document, and two trailing
      // spaces are a hard break.
      const contentIndent = indentStr + " ".repeat(markerWidth);
      const indentLines = (text: string, indentFirstLine: boolean): string =>
        text
          .split("\n")
          .map((line, lineIndex) =>
            (lineIndex === 0 && !indentFirstLine) || line === "" ? line : contentIndent + line
          )
          .join("\n");

      // The item's children, in authored order. The first shares the marker line.
      //
      // Every block after it gets a line of its own. Joining one onto the marker line
      // with a space is what made this route non-idempotent (NEX-4602): a fenced code
      // block moved off column 0 stops being a fence, the next parse read ```` ```json ````
      // as an inline code span, the serializer escaped the backticks it now saw as prose,
      // and the closing fence was left dangling as an empty block that crashed the
      // prompt editor. A blockquote moved onto the line above it lost one newline per
      // save forever.
      //
      // A nested list keeps its place among them too. Holding the lists back to the end
      // of the item moved them below any block written after them — one production
      // prompt had the steps of its first message land under its second message's card.
      //
      // Except in an item that OPENS on a list, which has no markdown marked reads back:
      // `- ` with nothing after it does not start a list, and a bare `-` under a line of
      // text is a setext heading underline. The editor never stores one — its list item
      // opens on a paragraph — but a document parsed from `- - x` can. That item keeps the
      // layout it always had, its first other block on the marker line and its lists
      // after, which reorders it but keeps every node.
      const children = item.content || [];
      const ordered =
        children[0] && isNestedList(children[0])
          ? [...children.filter((c) => !isNestedList(c)), ...children.filter(isNestedList)]
          : children;

      let itemText = "";
      let isFirstChild = true;
      for (const child of ordered) {
        if (isNestedList(child)) {
          // Pass orderedListType for inheritance. The nested list indents itself, and
          // sits directly below the line above it, as it always has.
          const nestedText = serializeListContent(
            child,
            indent + 1,
            listType === NODE_NAMES.ORDERED_LIST ? orderedListType : undefined,
            hooks
          );
          if (!nestedText) continue;
          itemText += `\n${nestedText}`;
          isFirstChild = false;
          continue;
        }
        // A paragraph goes through `renderInline`, not `renderBlock`. The two are the
        // same for the backend, but the frontend's block renderer consults its custom
        // node renderers first, and a list item's paragraph has never been offered to
        // those — routing it there now would change what the editor emits.
        const text =
          child.type === NODE_NAMES.PARAGRAPH ? renderInline(child.content) : renderBlock(child);
        if (!text) continue;
        itemText += isFirstChild ? indentLines(text, false) : `\n\n${indentLines(text, true)}`;
        isFirstChild = false;
      }

      return `${indentStr}${prefix} ${itemText}`;
    })
    .filter(Boolean)
    .join("\n");
}

// ============================================================================
// Block Content Serialization
// ============================================================================

/**
 * Emits a fenced code block whose fence is long enough to hold its own content.
 *
 * A fenced block ends at the first line whose fence is at least as long as the
 * opening one, so a three-backtick fence cannot hold content that itself contains
 * ` ``` ` — the block closes early and everything after it becomes a second, empty
 * block. That is one of the two shapes that crashed the prompt editor in NEX-4602;
 * the other was a code block flattened onto a list marker line. 12 of the 21 code
 * blocks in the production prompt that reported this already hold such a run,
 * swallowed there by an earlier round of the same bug.
 *
 * Scanning for the longest run anywhere rather than only at line starts is
 * deliberate: a run mid-line cannot close a fence, but leaving it out would make the
 * fence depend on where inside the content the run sits, and a code block that
 * re-fences itself after an unrelated edit is the drift this module exists to
 * prevent.
 *
 * Shared with the frontend MarkdownManager so both pipelines fence identically.
 */
export function serializeCodeBlock(language: string, code: string): string {
  let longest = 0;
  for (const match of code.matchAll(/`+/g)) {
    longest = Math.max(longest, match[0].length);
  }
  const fence = "`".repeat(Math.max(3, longest + 1));
  return `${fence}${language}\n${code}\n${fence}`;
}

/**
 * Serializes a single block node (excluding empty paragraphs)
 */
function serializeSingleBlock(node: TipTapJSONContent): string | null {
  if (!node.type) return null;

  switch (node.type) {
    case NODE_NAMES.PARAGRAPH:
      return serializeInline(node.content);

    case NODE_NAMES.HEADING: {
      // Output heading level as-is (TipTap schema only allows 1-3, no need to clamp)
      const level = (node.attrs?.level as number) || 1;
      const prefix = "#".repeat(level);
      const headingText = serializeInline(node.content);
      return `${prefix} ${headingText}`;
    }

    case NODE_NAMES.BLOCKQUOTE: {
      const blockquoteText = serializeBlocks(node.content);
      // Prefix each line with >
      return blockquoteText
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n");
    }

    case NODE_NAMES.CODE_BLOCK: {
      // Code block with optional language for syntax highlighting
      // Use raw text (no escaping) — markdown treats code block content as literal.
      // Escaping would survive the round-trip and accumulate on each copy/paste cycle.
      const language = (node.attrs?.language as string) || "";
      return serializeCodeBlock(language, serializeRawText(node.content));
    }

    case NODE_NAMES.BULLET_LIST:
    case NODE_NAMES.ORDERED_LIST:
    case NODE_NAMES.TASK_LIST:
      return serializeListContent(node, 0);

    case NODE_NAMES.COLLECTION_MENTION: {
      // Collection mentions are block-level - format as %%name%%
      const collectionName = (node.attrs?.name as string) || "";
      if (!collectionName) return null; // Skip empty mentions (return null for blocks)
      return `%%${collectionName}%%`;
    }

    default:
      // Unknown block types - warn and skip
      if (node.type !== NODE_NAMES.DOC) {
        // eslint-disable-next-line no-console -- intentional diagnostic for an unexpected node type
        console.warn(`[markdown-serializer] Unknown block type: ${node.type}`);
      }
      // Recurse into content if available
      if (node.content) {
        return serializeBlocks(node.content);
      }
      return null;
  }
}

/**
 * Serializes an array of block nodes to markdown
 *
 * Each block in the editor (paragraph, heading, list, etc.) becomes one "line" in markdown.
 * Lines are separated by \n\n (standard markdown paragraph break).
 *
 * Empty paragraphs are serialized as empty strings "", which when joined with \n\n
 * produce the blank lines needed for visual spacing.
 *
 * Examples:
 * - [A, B] → "A\n\nB" (two paragraphs)
 * - [A, empty, B] → "A\n\n\n\nB" (blank line between = extra \n\n)
 * - [empty] → "" (single empty paragraph = empty string)
 * - [empty, empty] → "\n\n" (two empty paragraphs = one blank line)
 *
 * @param content - Array of block content nodes
 * @returns Serialized markdown string with proper spacing
 */
function serializeBlocks(content: TipTapJSONContent[] | undefined): string {
  if (!content) return "";

  const lines: string[] = [];

  for (const node of content) {
    if (isEmptyParagraph(node)) {
      // Empty paragraph becomes empty string
      // When joined with \n\n, this creates blank lines
      lines.push("");
    } else {
      const serialized = serializeSingleBlock(node);
      if (serialized !== null) {
        lines.push(serialized);
      }
    }
  }

  // Join with \n\n (paragraph separator in markdown)
  return lines.join("\n\n");
}

// ============================================================================
// Tab Panel Serialization
// ============================================================================

/**
 * Serializes a tab panel with its deployment type marker
 *
 * Each block in the tab takes one line in markdown.
 * An empty paragraph is represented as a blank line.
 *
 * @param tabPanel - The tab panel node
 * @returns Markdown string with tab marker and content
 */
function serializeTabPanel(tabPanel: TipTapJSONContent): string {
  const attrs = tabPanel.attrs as SerializerTabPanelAttrs | undefined;
  const deploymentType = (attrs?.deploymentTypes as string[] | undefined)?.join("+") ?? "NEXUS";

  const marker = buildTabDirective(deploymentType);
  const content = serializeBlocks(tabPanel.content);

  // Always add newline after marker if there's content (even empty string = empty paragraph)
  // Tab with no content at all: just marker
  // Tab with empty paragraph(s): marker + newline + empty line(s)
  // Tab with content: marker + newline + content
  if (tabPanel.content && tabPanel.content.length > 0) {
    return `${marker}\n${content}`;
  }
  return marker;
}

// ============================================================================
// Section Serialization
// ============================================================================

/**
 * Extracts the title text from a section node
 *
 * @param section - The section node
 * @returns The section title or empty string
 */
function extractSectionTitle(section: TipTapJSONContent): string {
  if (!section.content) return "";

  // Find the sectionTitle child
  const titleNode = section.content.find((child) => child.type === NODE_NAMES.SECTION_TITLE);
  if (!titleNode) return "";

  // Raw, NOT markdown-escaped. The title lands inside the `name="..."` directive
  // attribute, which is not a markdown context — `serializeSection` escapes it for the
  // quoted string (`\` and `"`) and the parser stores the result as a plain text node
  // without markdown-parsing it. Markdown-escaping here added a second escape layer
  // that the parser never undid, so `\` grew on every save (NEX-2564: a live section
  // title reached a 42-backslash run). One escape layer, one inverse.
  return serializeRawText(titleNode.content);
}

/**
 * Serializes a section with its metadata comment and all tabs
 *
 * @param section - The section node to serialize
 * @returns Markdown string with metadata and content
 */
function serializeSection(section: TipTapJSONContent): string {
  const attrs = section.attrs as SerializerSectionAttrs | undefined;

  // Extract title from sectionTitle child node
  const title = extractSectionTitle(section);

  // Build metadata directive
  // Format: ::: section: name="Section Title", deploymentSpecific=false, readonly=false, hidden=false, defaultConfig=false :::
  const sectionMetadata: string[] = [];
  // Name is the section title (required first for readability)
  const escapedTitle = (title || "")
    .replace(/\\/g, "\\\\") // Escape backslashes FIRST
    .replace(/"/g, '\\"'); // Then escape quotes
  sectionMetadata.push(`name="${escapedTitle}"`);
  sectionMetadata.push(`deploymentSpecific=${attrs?.isDeploymentSpecific === true}`);
  sectionMetadata.push(`readonly=${attrs?.isReadOnly === true}`);
  sectionMetadata.push(`hidden=${attrs?.isHidden === true}`);
  sectionMetadata.push(`defaultConfig=${attrs?.isDefaultConfigSection === true}`);

  // Find tabContainer to serialize tabs
  const tabContainer = section.content?.find((child) => child.type === NODE_NAMES.TAB_CONTAINER);

  const metaDirective = buildSectionDirective(sectionMetadata.join(", "));

  if (!tabContainer?.content) {
    // No tab container - just serialize any other content
    const otherContent = section.content?.filter(
      (child) => child.type !== NODE_NAMES.SECTION_TITLE && child.type !== NODE_NAMES.TAB_CONTAINER
    );
    const contentStr = serializeBlocks(otherContent);
    return contentStr ? `${metaDirective}\n\n${contentStr}` : metaDirective;
  }

  // Serialize all tab panels
  // Tabs are joined with newlines between them
  const tabsContent = tabContainer.content
    .filter((child) => child.type === NODE_NAMES.TAB_PANEL)
    .map((tabPanel) => serializeTabPanel(tabPanel))
    .join("\n");

  // Section directive followed immediately by tabs (no blank line)
  return `${metaDirective}\n${tabsContent}`;
}

// ============================================================================
// Main Export
// ============================================================================

/**
 * Serializes TipTap JSON content to Markdown format
 *
 * The output format includes:
 * - Section metadata as HTML comments
 * - Tab markers for deployment-specific content
 * - Standard markdown for content (headings, lists, formatting)
 * - Special syntax for mentions ({{variable}}, @skill, [placeholder])
 *
 * @param json - The TipTap editor JSON content
 * @returns Full markdown string representation
 *
 * @example
 * ```typescript
 * const markdown = serializeToMarkdown(editor.getJSON())
 * console.log(markdown)
 * // ::: section: name="My Section", deploymentSpecific=false, ... :::
 * //
 * // ::: tab: NEXUS :::
 * // # Heading
 * //
 * // Some text with {{variable}} mention
 * //
 * // ---
 * //
 * // ::: section: name="Another Section", ... :::
 * // ...
 * ```
 */
export function serializeToMarkdown(json: TipTapJSONContent | null): string {
  if (!json || !json.content) return "";

  // Filter for section nodes only
  const sections = json.content.filter((node) => node.type === NODE_NAMES.SECTION);

  const markdown =
    sections.length === 0
      ? // No sections found - treat as raw content
        serializeBlocks(json.content)
      : // Serialize each section and join with single newline
        // (each section's last tab already ends with \n, so \n creates blank line separator)
        sections
          .map((section) => serializeSection(section))
          .filter(Boolean)
          .join("\n");

  // Strip invalid C0 control characters so the output is always valid RFC 8259
  // JSON once stringified, and never carries non-printable garbage (NEX-2289).
  return stripInvalidControlChars(markdown);
}
